import { Controller } from "@hotwired/stimulus"

// Fullscreen background video cycler for the hero section.
//
// Markup contract:
//   <section data-controller="background"
//            data-background-sources-value='["/videos/a.mp4", "/videos/b.mp4"]'>
//     <img … poster …>                          static fallback, always present
//     <div data-background-target="stage"></div> videos get layered in here
//     <div … overlay …>                         readability overlay
//     …content…
//   </section>
//
// Videos are muted and played in sequence, crossfading between them. A source
// that fails to load is skipped; if none can play, the static poster remains.
// Honors prefers-reduced-motion by not starting playback at all.
export default class extends Controller {
  static values = { sources: Array }
  static targets = ["stage"]

  connect() {
    if (!Array.isArray(this.sourcesValue) || this.sourcesValue.length === 0) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    const stage = this.hasStageTarget ? this.stageTarget : this.element

    this.index = -1
    this.layers = this.sourcesValue.map((source, i) => {
      const video = document.createElement("video")
      video.src = source
      video.muted = true
      video.playsInline = true
      video.preload = i === 0 ? "auto" : "metadata"
      video.setAttribute("muted", "")
      video.setAttribute("playsinline", "")
      video.setAttribute("aria-hidden", "true")
      video.tabIndex = -1
      video.classList.add(
        "absolute", "inset-0", "h-full", "w-full",
        "object-cover", "opacity-0",
        "transition-opacity", "duration-1000", "ease-in-out"
      )

      const layer = { video, broken: false }

      video.addEventListener("ended", () => this.#show(this.index + 1))
      video.addEventListener("error", () => {
        layer.broken = true
        video.remove()
        if (this.index === i) this.#show(i + 1)
      })

      stage.appendChild(video)
      return layer
    })

    if (this.layers.length === 1) this.layers[0].video.loop = true

    this.#show(0)
  }

  disconnect() {
    clearTimeout(this.fadeTimer)
    this.layers?.forEach(({ video }) => {
      video.pause()
      video.remove()
    })
    this.layers = null
  }

  // Crossfades to the next healthy layer starting the search at `from`.
  async #show(from) {
    if (!this.layers) return

    const index = this.#nextHealthy(from)
    if (index === -1) return

    const layer = this.layers[index]
    if (index === this.index) {
      layer.video.currentTime = 0
      layer.video.play().catch(() => {})
      return
    }

    try {
      await this.#load(layer)
    } catch {
      layer.broken = true
      layer.video.remove()
      this.#show(index + 1)
      return
    }

    const previous = this.index >= 0 ? this.layers[this.index] : null
    this.index = index

    layer.video.classList.add("opacity-100")
    layer.video.play().catch(() => {})

    // Let the crossfade finish, then reset the outgoing video.
    clearTimeout(this.fadeTimer)
    this.fadeTimer = setTimeout(() => {
      if (previous && previous !== layer && !previous.broken) {
        previous.video.pause()
        previous.video.currentTime = 0
        previous.video.classList.remove("opacity-100")
      }
    }, 1100)

    this.#preload(index + 1)
  }

  // First non-broken layer starting at `from` (wraps around), or -1 if none.
  #nextHealthy(from) {
    for (let n = 0; n < this.layers.length; n++) {
      const i = (((from + n) % this.layers.length) + this.layers.length) % this.layers.length
      if (!this.layers[i].broken) return i
    }
    return -1
  }

  // Resolves once the layer's video is ready to start playing.
  #load(layer) {
    if (layer.video.readyState >= 3) return Promise.resolve()

    return new Promise((resolve, reject) => {
      layer.video.addEventListener("canplay", resolve, { once: true })
      layer.video.addEventListener("error", reject, { once: true })
      layer.video.load()
    })
  }

  // Warms up the video that will be shown next.
  #preload(from) {
    const index = this.#nextHealthy(from)
    if (index === -1 || index === this.index) return

    const { video } = this.layers[index]
    if (video.preload !== "auto") {
      video.preload = "auto"
      video.load()
    }
  }
}
